import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { DialogShell } from "@/shared/ui/DialogShell";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import { VideoData, VideoStatus } from "../../../shared/interfaces/VideoData";
import { TranscriptionResult } from "../hooks/useTranscription";

interface Props {
  /** Selection dialog: choose which videos to transcribe. */
  selectOpen: boolean;
  onCloseSelect: () => void;
  videos: VideoData[];
  onConfirm: (videos: VideoData[]) => void;

  /** View dialog: choose which finished transcript to open. */
  viewOpen: boolean;
  onCloseView: () => void;
  results: TranscriptionResult[];
  onView: (evidenceId: string) => void;
}

/** Shows a small status chip for a video's server-side transcription state. */
const statusChip = (video: VideoData): React.ReactNode => {
  if (!video.videoUrl) {
    return <Chip size="small" color="default" label="Not downloaded yet" />;
  }
  if (video.status === VideoStatus.TRANSCRIPT_READY) {
    return <Chip size="small" color="success" label="Transcript ready" />;
  }
  if (
    video.status === VideoStatus.TRANSCRIBING ||
    video.status === VideoStatus.TRANSCRIPTION_QUEUED
  ) {
    return <Chip size="small" color="warning" label="Transcribing" />;
  }
  return null;
};

export const TranscribeVideosDialog: React.FC<Props> = ({
  selectOpen,
  onCloseSelect,
  videos,
  onConfirm,
  viewOpen,
  onCloseView,
  results,
  onView,
}) => {
  // Videos with no file in S3 yet (no videoUrl) can't transcribe — their rows
  // are disabled and they never default-check.
  const transcribable = useMemo(
    () => videos.filter((v) => !!v.videoUrl),
    [videos],
  );

  // Default every transcribable video checked when the selection dialog (re)opens.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (selectOpen) {
      setSelectedIds(new Set(transcribable.map((v) => v.id)));
    }
  }, [selectOpen, transcribable]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected =
    transcribable.length > 0 && selectedIds.size === transcribable.length;
  const toggleAll = () => {
    setSelectedIds(
      allSelected ? new Set() : new Set(transcribable.map((v) => v.id)),
    );
  };

  const chosen = useMemo(
    () => transcribable.filter((v) => selectedIds.has(v.id)),
    [transcribable, selectedIds],
  );

  return (
    <>
      {/* Selection */}
      <DialogShell
        open={selectOpen}
        onClose={onCloseSelect}
        size="sm"
        titleId="transcribe-videos-dialog-title"
      >
        <DialogTitle id="transcribe-videos-dialog-title" sx={{ pr: 6 }}>
          Transcribe videos
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the videos you want to transcribe. Transcription runs in the
            background and can take a few minutes per video.
          </DialogContentText>
          {videos.length > 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.size > 0 && !allSelected}
                  onChange={toggleAll}
                />
              }
              label="Select all"
            />
          )}
          <FormGroup>
            {videos.map((video) => (
              <FormControlLabel
                key={video.id}
                disabled={!video.videoUrl}
                control={
                  <Checkbox
                    checked={selectedIds.has(video.id)}
                    onChange={() => toggle(video.id)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{video.title}</Typography>
                    {statusChip(video)}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSelect}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SubtitlesIcon />}
            disabled={chosen.length === 0}
            onClick={() => onConfirm(chosen)}
          >
            Transcribe{chosen.length > 0 ? ` (${chosen.length})` : ""}
          </Button>
        </DialogActions>
      </DialogShell>

      {/* Choose which transcript to view */}
      <DialogShell
        open={viewOpen}
        onClose={onCloseView}
        size="sm"
        titleId="transcription-complete-dialog-title"
      >
        <DialogTitle id="transcription-complete-dialog-title" sx={{ pr: 6 }}>
          Transcription complete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Choose a transcript to open. Videos that failed to transcribe are
            listed with the reason.
          </DialogContentText>
          <List>
            {results.map(({ video, ok, message }) =>
              ok ? (
                <ListItemButton
                  key={video.id}
                  onClick={() => onView(video.id)}
                >
                  <CheckCircleIcon color="success" sx={{ mr: 1.5 }} />
                  <ListItemText primary={video.title} secondary="Ready to view" />
                </ListItemButton>
              ) : (
                <ListItem key={video.id} sx={{ opacity: 0.75 }}>
                  <ErrorOutlineIcon color="error" sx={{ mr: 1.5 }} />
                  <ListItemText
                    primary={video.title}
                    secondary={message}
                    secondaryTypographyProps={{ color: "error" }}
                  />
                </ListItem>
              ),
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseView}>Close</Button>
        </DialogActions>
      </DialogShell>
    </>
  );
};
